import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useMeeting, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  ClipboardList,
  Search,
  Calendar,
  Building2,
  User,
  Zap,
  Settings2,
  Cpu,
  FileText,
  Download,
  Eye,
  Loader2,
  ChevronRight,
  Clock,
  CheckCircle2,
  AlertCircle,
  Printer
} from "lucide-react";
import { toast } from "sonner";

const SessionsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { setCurrentMeeting } = useMeeting();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSession, setSelectedSession] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (token) {
      fetchSessions();
    }
  }, [token]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/meetings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data);
    } catch (error) {
      console.error("Failed to fetch sessions:", error);
      toast.error("Failed to load sessions");
    } finally {
      setLoading(false);
    }
  };

  const viewSessionDetail = async (session) => {
    try {
      setDetailLoading(true);
      const response = await axios.get(`${API}/meetings/${session.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedSession(response.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error("Failed to load session details");
    } finally {
      setDetailLoading(false);
    }
  };

  const resumeSession = (session) => {
    setCurrentMeeting(session);
    navigate("/dashboard");
  };

  const getAssetIcon = (assetType) => {
    switch (assetType?.toLowerCase()) {
      case "transformers":
        return <Zap className="w-4 h-4 text-yellow-600" />;
      case "switchgear":
        return <Settings2 className="w-4 h-4 text-blue-600" />;
      case "motors":
        return <Cpu className="w-4 h-4 text-green-600" />;
      default:
        return <Zap className="w-4 h-4 text-slate-400" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getSessionStatus = (session) => {
    const assetCount = session.selected_assets?.length || 0;
    const hasNotes = session.notes && session.notes.trim().length > 0;
    
    if (assetCount > 0 && hasNotes) {
      return { label: "Complete", color: "bg-green-100 text-green-700 border-green-200" };
    } else if (assetCount > 0 || hasNotes) {
      return { label: "In Progress", color: "bg-yellow-100 text-yellow-700 border-yellow-200" };
    }
    return { label: "New", color: "bg-slate-100 text-slate-600 border-slate-200" };
  };

  const downloadSessionSummary = (session) => {
    const status = getSessionStatus(session);
    const assetCount = session.selected_assets?.length || 0;
    
    // Group assets by type
    const assetsByType = {};
    (session.selected_assets || []).forEach(asset => {
      const type = asset.asset || "Other";
      if (!assetsByType[type]) assetsByType[type] = [];
      assetsByType[type].push(asset);
    });

    const summaryContent = `
================================================================================
                           SESSION SUMMARY REPORT
================================================================================

SESSION INFORMATION
-------------------
Customer Name:    ${session.customer_name || "N/A"}
Company:          ${session.company || "N/A"}
Primary Focus:    ${session.primary_asset_focus || "N/A"}
Session Date:     ${formatDate(session.created_at)} at ${formatTime(session.created_at)}
Last Updated:     ${formatDate(session.updated_at)} at ${formatTime(session.updated_at)}
Status:           ${status.label}

================================================================================

ASSETS DISCUSSED (${assetCount} total)
----------------------------------------
${Object.entries(assetsByType).map(([type, assets]) => `
${type.toUpperCase()}:
${assets.map(a => `  • ${a.type}: ${a.id}`).join("\n")}
`).join("\n") || "No assets selected"}

================================================================================

SESSION NOTES
-------------
${session.notes || "No notes recorded"}

================================================================================

DISCUSSION POINTS
-----------------
${(session.discussion_points || []).map((p, i) => `${i + 1}. ${p}`).join("\n") || "None recorded"}

================================================================================

QUESTIONS RAISED
----------------
${(session.questions || []).map((q, i) => `${i + 1}. ${q}`).join("\n") || "None recorded"}

================================================================================

ACTION ITEMS
------------
${(session.action_items || []).map((a, i) => `${i + 1}. ${a}`).join("\n") || "None recorded"}

================================================================================

                    Generated on ${new Date().toLocaleString()}
                           Asset Atlas - CBM Diagnostic Engine

================================================================================
`;

    // Create and download file
    const blob = new Blob([summaryContent], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Session_${session.company?.replace(/\s+/g, "_") || "Unknown"}_${formatDate(session.created_at).replace(/\s+/g, "_")}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success("Session summary downloaded");
  };

  const printSessionSummary = (session) => {
    const status = getSessionStatus(session);
    const assetCount = session.selected_assets?.length || 0;
    
    const assetsByType = {};
    (session.selected_assets || []).forEach(asset => {
      const type = asset.asset || "Other";
      if (!assetsByType[type]) assetsByType[type] = [];
      assetsByType[type].push(asset);
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Session Summary - ${session.company}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          h1 { color: #1e40af; border-bottom: 2px solid #1e40af; padding-bottom: 10px; }
          h2 { color: #334155; margin-top: 30px; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px; }
          .info-grid { display: grid; grid-template-columns: 150px 1fr; gap: 8px; margin: 20px 0; }
          .label { color: #64748b; font-weight: 500; }
          .value { color: #1e293b; }
          .status { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 500; }
          .status-complete { background: #dcfce7; color: #166534; }
          .status-progress { background: #fef9c3; color: #854d0e; }
          .status-new { background: #f1f5f9; color: #475569; }
          .asset-section { margin: 15px 0; padding: 15px; background: #f8fafc; border-radius: 8px; }
          .asset-type { font-weight: 600; color: #1e40af; margin-bottom: 8px; }
          .asset-item { margin-left: 20px; color: #475569; }
          .notes-section { background: #fffbeb; padding: 20px; border-radius: 8px; margin: 20px 0; }
          .list-section { margin: 15px 0; }
          .list-item { margin: 8px 0; padding-left: 20px; position: relative; }
          .list-item:before { content: "•"; position: absolute; left: 0; color: #1e40af; }
          .footer { margin-top: 40px; text-align: center; color: #94a3b8; font-size: 12px; border-top: 1px solid #e2e8f0; padding-top: 20px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <h1>Session Summary Report</h1>
        
        <div class="info-grid">
          <span class="label">Customer:</span>
          <span class="value">${session.customer_name || "N/A"}</span>
          <span class="label">Company:</span>
          <span class="value">${session.company || "N/A"}</span>
          <span class="label">Primary Focus:</span>
          <span class="value">${session.primary_asset_focus || "N/A"}</span>
          <span class="label">Session Date:</span>
          <span class="value">${formatDate(session.created_at)} at ${formatTime(session.created_at)}</span>
          <span class="label">Status:</span>
          <span class="value"><span class="status status-${status.label.toLowerCase().replace(" ", "-")}">${status.label}</span></span>
        </div>

        <h2>Assets Discussed (${assetCount})</h2>
        ${Object.entries(assetsByType).length > 0 ? Object.entries(assetsByType).map(([type, assets]) => `
          <div class="asset-section">
            <div class="asset-type">${type}</div>
            ${assets.map(a => `<div class="asset-item">${a.type}: ${a.id}</div>`).join("")}
          </div>
        `).join("") : "<p>No assets selected</p>"}

        <h2>Session Notes</h2>
        <div class="notes-section">
          ${session.notes || "<em>No notes recorded</em>"}
        </div>

        ${(session.discussion_points?.length > 0) ? `
          <h2>Discussion Points</h2>
          <div class="list-section">
            ${session.discussion_points.map(p => `<div class="list-item">${p}</div>`).join("")}
          </div>
        ` : ""}

        ${(session.questions?.length > 0) ? `
          <h2>Questions Raised</h2>
          <div class="list-section">
            ${session.questions.map(q => `<div class="list-item">${q}</div>`).join("")}
          </div>
        ` : ""}

        ${(session.action_items?.length > 0) ? `
          <h2>Action Items</h2>
          <div class="list-section">
            ${session.action_items.map(a => `<div class="list-item">${a}</div>`).join("")}
          </div>
        ` : ""}

        <div class="footer">
          Generated on ${new Date().toLocaleString()}<br>
          Asset Atlas - CBM Diagnostic Engine
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const filteredSessions = sessions.filter(session =>
    session.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    session.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    session.primary_asset_focus?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group sessions by month
  const groupedSessions = filteredSessions.reduce((groups, session) => {
    const date = new Date(session.created_at);
    const monthYear = date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    if (!groups[monthYear]) groups[monthYear] = [];
    groups[monthYear].push(session);
    return groups;
  }, {});

  return (
    <div className="space-y-6" data-testid="sessions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <ClipboardList className="w-7 h-7 text-blue-600" />
            Session History
          </h1>
          <p className="text-slate-500 mt-1">
            Review past sessions, download summaries, and plan follow-ups
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search sessions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-64"
              data-testid="session-search"
            />
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border p-4">
          <div className="text-2xl font-bold text-slate-900">{sessions.length}</div>
          <div className="text-sm text-slate-500">Total Sessions</div>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="text-2xl font-bold text-green-600">
            {sessions.filter(s => getSessionStatus(s).label === "Complete").length}
          </div>
          <div className="text-sm text-slate-500">Completed</div>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="text-2xl font-bold text-yellow-600">
            {sessions.filter(s => getSessionStatus(s).label === "In Progress").length}
          </div>
          <div className="text-sm text-slate-500">In Progress</div>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="text-2xl font-bold text-blue-600">
            {new Set(sessions.map(s => s.company)).size}
          </div>
          <div className="text-sm text-slate-500">Unique Companies</div>
        </div>
      </div>

      {/* Sessions List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : filteredSessions.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border">
          <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-700 mb-2">No sessions found</h3>
          <p className="text-slate-500 mb-4">
            {searchTerm ? "Try a different search term" : "Start a new session from the landing page"}
          </p>
          {!searchTerm && (
            <Button onClick={() => navigate("/landing")} className="bg-blue-600 hover:bg-blue-700">
              Start New Session
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedSessions).map(([monthYear, monthSessions]) => (
            <div key={monthYear}>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {monthYear}
                <span className="text-slate-400 font-normal">({monthSessions.length} sessions)</span>
              </h3>
              <div className="space-y-2">
                {monthSessions.map((session) => {
                  const status = getSessionStatus(session);
                  const assetCount = session.selected_assets?.length || 0;
                  
                  return (
                    <div
                      key={session.id}
                      className="bg-white rounded-lg border hover:border-blue-200 hover:shadow-sm transition-all p-4"
                      data-testid={`session-${session.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {/* Asset Icon */}
                          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                            {getAssetIcon(session.primary_asset_focus)}
                          </div>
                          
                          {/* Session Info */}
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-slate-900">{session.company}</h4>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${status.color}`}>
                                {status.label}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                              <span className="flex items-center gap-1">
                                <User className="w-3.5 h-3.5" />
                                {session.customer_name}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" />
                                {formatDate(session.created_at)}
                              </span>
                              {assetCount > 0 && (
                                <span className="flex items-center gap-1">
                                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                                  {assetCount} assets
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => viewSessionDetail(session)}
                            className="text-slate-500 hover:text-blue-600"
                            title="View Details"
                            data-testid={`view-session-${session.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadSessionSummary(session)}
                            className="text-slate-500 hover:text-green-600"
                            title="Download Summary"
                            data-testid={`download-session-${session.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => printSessionSummary(session)}
                            className="text-slate-500 hover:text-purple-600"
                            title="Print Summary"
                          >
                            <Printer className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => resumeSession(session)}
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            data-testid={`resume-session-${session.id}`}
                          >
                            Resume
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      </div>

                      {/* Notes Preview */}
                      {session.notes && (
                        <div className="mt-3 pt-3 border-t text-sm text-slate-600 line-clamp-2">
                          <span className="font-medium text-slate-500">Notes:</span> {session.notes}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Session Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Session Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedSession && (
            <div className="space-y-4 mt-4">
              {/* Header Info */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-lg text-slate-900">{selectedSession.company}</h3>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getSessionStatus(selectedSession).color}`}>
                    {getSessionStatus(selectedSession).label}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-500">Customer:</span>
                    <span className="ml-2 text-slate-700">{selectedSession.customer_name}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Primary Focus:</span>
                    <span className="ml-2 text-slate-700">{selectedSession.primary_asset_focus}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Created:</span>
                    <span className="ml-2 text-slate-700">{formatDate(selectedSession.created_at)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Last Updated:</span>
                    <span className="ml-2 text-slate-700">{formatDate(selectedSession.updated_at)}</span>
                  </div>
                </div>
              </div>

              {/* Assets */}
              {selectedSession.selected_assets?.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-700 mb-2">Assets Discussed ({selectedSession.selected_assets.length})</h4>
                  <div className="space-y-1">
                    {selectedSession.selected_assets.map((asset, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm p-2 bg-slate-50 rounded">
                        {getAssetIcon(asset.asset)}
                        <span className="text-slate-600">{asset.asset}</span>
                        <span className="text-slate-400">•</span>
                        <span className="text-slate-500">{asset.type}: {asset.id}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedSession.notes && (
                <div>
                  <h4 className="font-medium text-slate-700 mb-2">Session Notes</h4>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-slate-700">
                    {selectedSession.notes}
                  </div>
                </div>
              )}

              {/* Discussion Points */}
              {selectedSession.discussion_points?.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-700 mb-2">Discussion Points</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm text-slate-600">
                    {selectedSession.discussion_points.map((point, idx) => (
                      <li key={idx}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Questions */}
              {selectedSession.questions?.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-700 mb-2">Questions Raised</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm text-slate-600">
                    {selectedSession.questions.map((q, idx) => (
                      <li key={idx}>{q}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Action Items */}
              {selectedSession.action_items?.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-700 mb-2">Action Items</h4>
                  <ul className="space-y-1">
                    {selectedSession.action_items.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => downloadSessionSummary(selectedSession)}
                  className="flex-1"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
                <Button
                  variant="outline"
                  onClick={() => printSessionSummary(selectedSession)}
                  className="flex-1"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
                <Button
                  onClick={() => {
                    setShowDetailModal(false);
                    resumeSession(selectedSession);
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  Resume Session
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SessionsPage;
